/**
 * Identity Manager Modal - Full management UI for identities
 * Allows editing, deletion, export/import of identities
 */

import React, { useState, useRef } from 'react';
import { useIdentity } from './use-identity';
import { formatIdentity } from '@maco144/shared';

interface IdentityManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabType = 'list' | 'import';

export function IdentityManager({ isOpen, onClose }: IdentityManagerProps): JSX.Element {
  const [tab, setTab] = useState<TabType>('list');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [exportingId, setExportingId] = useState<string | null>(null);
  const [exportPassword, setExportPassword] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [importData, setImportData] = useState('');
  const [importPassword, setImportPassword] = useState('');
  const [importName, setImportName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    identities,
    updateIdentity,
    deleteIdentity,
    exportIdentity,
    importIdentity,
    isLoading,
    error,
    clearError,
  } = useIdentity();

  if (!isOpen) return <div />;

  const handleSaveEdit = async (userId: string) => {
    if (!editingName.trim()) {
      alert('Identity name cannot be empty');
      return;
    }

    try {
      await updateIdentity(userId, { name: editingName });
      setEditingId(null);
      setEditingName('');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update identity');
    }
  };

  const handleExport = async (userId: string) => {
    try {
      const data = await exportIdentity(userId);

      // Create download link
      const element = document.createElement('a');
      element.setAttribute('href', `data:text/plain;charset=utf-8,${encodeURIComponent(data)}`);
      const identity = identities.find((i) => i.userId === userId);
      const filename = `rising-sun-identity-${identity?.name || 'backup'}.json`;
      element.setAttribute('download', filename);
      element.style.display = 'none';
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);

      setExportingId(null);
      setExportPassword('');
      alert('Identity exported successfully!');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to export identity');
    }
  };

  const handleDelete = async (userId: string) => {
    try {
      await deleteIdentity(userId);
      setDeleteConfirmId(null);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete identity');
    }
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      setImportData(text);
    } catch {
      alert('Failed to read file');
    }
  };

  const handleImport = async () => {
    if (!importData.trim()) {
      alert('Please select a backup file');
      return;
    }

    if (!importName.trim()) {
      alert('Please enter a name for this identity');
      return;
    }

    if (!importPassword.trim()) {
      alert('Please enter a password for this identity');
      return;
    }

    try {
      await importIdentity(importData, importName, importPassword);
      setImportData('');
      setImportName('');
      setImportPassword('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setTab('list');
      alert('Identity imported successfully!');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to import identity');
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '20px',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'rgba(30, 41, 59, 0.95)',
          border: '1px solid rgba(255, 255, 255, 0.15)',
          borderRadius: '20px',
          maxWidth: '500px',
          width: '100%',
          maxHeight: '80vh',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '24px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h2 style={{ margin: 0, color: '#e1e8ed', fontSize: '20px', fontWeight: '600' }}>
            Manage Identities
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#9ca3af',
              cursor: 'pointer',
              fontSize: '24px',
            }}
          >
            ✕
          </button>
        </div>

        {/* Tabs */}
        <div
          style={{
            display: 'flex',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            padding: '0 24px',
          }}
        >
          {(['list', 'import'] as const).map((t) => (
            <button
              key={t}
              onClick={() => {
                setTab(t);
                clearError();
              }}
              style={{
                flex: 1,
                padding: '16px',
                border: 'none',
                background: 'none',
                color: tab === t ? '#667eea' : '#9ca3af',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: t === tab ? '600' : '500',
                borderBottom: tab === t ? '2px solid #667eea' : 'none',
                transition: 'all 0.2s',
              }}
            >
              {t === 'list' ? 'My Identities' : 'Import Backup'}
            </button>
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <div
            style={{
              padding: '12px 24px',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              color: '#ef4444',
              fontSize: '13px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span>{error.message}</span>
            <button
              onClick={clearError}
              style={{
                background: 'none',
                border: 'none',
                color: '#ef4444',
                cursor: 'pointer',
              }}
            >
              ✕
            </button>
          </div>
        )}

        {/* Content */}
        <div style={{ padding: '24px' }}>
          {tab === 'list' ? (
            <div>
              {identities.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#9ca3af', padding: '24px' }}>
                  <p style={{ margin: 0 }}>No identities yet</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {identities.map((identity) => (
                    <div
                      key={identity.userId}
                      style={{
                        padding: '16px',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '12px',
                        background: 'rgba(255, 255, 255, 0.02)',
                      }}
                    >
                      {editingId === identity.userId ? (
                        <div>
                          <input
                            type="text"
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            placeholder="Display name (optional)"
                            style={{
                              width: '100%',
                              padding: '8px',
                              borderRadius: '6px',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              background: 'rgba(0, 0, 0, 0.2)',
                              color: '#e1e8ed',
                              marginBottom: '12px',
                              boxSizing: 'border-box',
                            }}
                          />
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => handleSaveEdit(identity.userId)}
                              style={{
                                flex: 1,
                                padding: '8px',
                                borderRadius: '6px',
                                border: 'none',
                                background: '#667eea',
                                color: '#fff',
                                cursor: 'pointer',
                                fontSize: '12px',
                              }}
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              style={{
                                flex: 1,
                                padding: '8px',
                                borderRadius: '6px',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                background: 'transparent',
                                color: '#e1e8ed',
                                cursor: 'pointer',
                                fontSize: '12px',
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : exportingId === identity.userId ? (
                        <div>
                          <div style={{ marginBottom: '12px' }}>
                            <label
                              style={{
                                display: 'block',
                                fontSize: '12px',
                                color: '#9ca3af',
                                marginBottom: '6px',
                              }}
                            >
                              Enter password to export:
                            </label>
                            <input
                              type="password"
                              value={exportPassword}
                              onChange={(e) => setExportPassword(e.target.value)}
                              placeholder="Password"
                              style={{
                                width: '100%',
                                padding: '8px',
                                borderRadius: '6px',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                background: 'rgba(0, 0, 0, 0.2)',
                                color: '#e1e8ed',
                                fontSize: '12px',
                                boxSizing: 'border-box',
                              }}
                            />
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => handleExport(identity.userId)}
                              disabled={isLoading}
                              style={{
                                flex: 1,
                                padding: '8px',
                                borderRadius: '6px',
                                border: 'none',
                                background: '#667eea',
                                color: '#fff',
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                fontSize: '12px',
                                opacity: isLoading ? 0.5 : 1,
                              }}
                            >
                              {isLoading ? 'Exporting...' : 'Export'}
                            </button>
                            <button
                              onClick={() => {
                                setExportingId(null);
                                setExportPassword('');
                              }}
                              style={{
                                flex: 1,
                                padding: '8px',
                                borderRadius: '6px',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                background: 'transparent',
                                color: '#e1e8ed',
                                cursor: 'pointer',
                                fontSize: '12px',
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : deleteConfirmId === identity.userId ? (
                        <div>
                          <p
                            style={{
                              color: '#ef4444',
                              fontSize: '13px',
                              margin: '0 0 12px 0',
                            }}
                          >
                            Are you sure? This will permanently delete this identity and all its
                            data.
                          </p>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => handleDelete(identity.userId)}
                              disabled={isLoading}
                              style={{
                                flex: 1,
                                padding: '8px',
                                borderRadius: '6px',
                                border: 'none',
                                background: '#ef4444',
                                color: '#fff',
                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                fontSize: '12px',
                                opacity: isLoading ? 0.5 : 1,
                              }}
                            >
                              {isLoading ? 'Deleting...' : 'Delete'}
                            </button>
                            <button
                              onClick={() => setDeleteConfirmId(null)}
                              style={{
                                flex: 1,
                                padding: '8px',
                                borderRadius: '6px',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                background: 'transparent',
                                color: '#e1e8ed',
                                cursor: 'pointer',
                                fontSize: '12px',
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div style={{ marginBottom: '12px' }}>
                            <div
                              style={{
                                color: '#e1e8ed',
                                fontWeight: '500',
                                fontSize: '14px',
                                marginBottom: '4px',
                              }}
                            >
                              {identity.name || formatIdentity(identity.phrase)}
                            </div>
                            <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                              {formatIdentity(identity.phrase)}
                            </div>
                            <div
                              style={{
                                fontSize: '11px',
                                color: '#6b7280',
                                marginTop: '6px',
                              }}
                            >
                              Created: {new Date(identity.createdAt).toLocaleDateString()}
                            </div>
                          </div>

                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => {
                                setEditingId(identity.userId);
                                setEditingName(identity.name || '');
                              }}
                              style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                border: '1px solid rgba(102, 126, 234, 0.3)',
                                background: 'rgba(102, 126, 234, 0.1)',
                                color: '#667eea',
                                cursor: 'pointer',
                                fontSize: '12px',
                                transition: 'all 0.2s',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(102, 126, 234, 0.2)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'rgba(102, 126, 234, 0.1)';
                              }}
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => setExportingId(identity.userId)}
                              style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                border: '1px solid rgba(107, 114, 128, 0.3)',
                                background: 'transparent',
                                color: '#d1d5db',
                                cursor: 'pointer',
                                fontSize: '12px',
                                transition: 'all 0.2s',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(107, 114, 128, 0.1)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                              }}
                            >
                              Export
                            </button>
                            <button
                              onClick={() => setDeleteConfirmId(identity.userId)}
                              style={{
                                padding: '6px 12px',
                                borderRadius: '6px',
                                border: '1px solid rgba(239, 68, 68, 0.3)',
                                background: 'transparent',
                                color: '#ef4444',
                                cursor: 'pointer',
                                fontSize: '12px',
                                transition: 'all 0.2s',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div>
              <div style={{ marginBottom: '16px' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#9ca3af',
                    marginBottom: '8px',
                  }}
                >
                  Select backup file
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleImportFile}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '6px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(0, 0, 0, 0.2)',
                    color: '#e1e8ed',
                    fontSize: '12px',
                  }}
                />
              </div>

              {importData && (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '12px',
                        fontWeight: '500',
                        color: '#9ca3af',
                        marginBottom: '8px',
                      }}
                    >
                      Identity password
                    </label>
                    <input
                      type="password"
                      value={importPassword}
                      onChange={(e) => setImportPassword(e.target.value)}
                      placeholder="Password"
                      style={{
                        width: '100%',
                        padding: '8px',
                        borderRadius: '6px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        background: 'rgba(0, 0, 0, 0.2)',
                        color: '#e1e8ed',
                        fontSize: '12px',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>

                  <button
                    onClick={handleImport}
                    disabled={isLoading}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: 'none',
                      background: '#667eea',
                      color: '#fff',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      opacity: isLoading ? 0.5 : 1,
                    }}
                  >
                    {isLoading ? 'Importing...' : 'Import Identity'}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Panel wrapper for Identity Manager - works with panel system
 * Always shows as open, simplified for panel use
 */
export function IdentityManagerPanel({ onClose }: { onClose?: () => void } = {}) {
  const { identities, currentIdentity, switchIdentity, logout, createIdentity, isLoading } =
    useIdentity();
  const [selectedPassword, setSelectedPassword] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Create identity form
  const [newAdjective, setNewAdjective] = useState('');
  const [newNoun, setNewNoun] = useState('');
  const [newVerb, setNewVerb] = useState('');
  const [newName, setNewName] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const handleCreateIdentity = async () => {
    if (!newAdjective || !newNoun || !newVerb || !newName || !newPassword) {
      alert('Please fill all fields');
      return;
    }

    try {
      await createIdentity(
        { adjective: newAdjective, noun: newNoun, verb: newVerb },
        newName,
        newPassword
      );
      setNewAdjective('');
      setNewNoun('');
      setNewVerb('');
      setNewName('');
      setNewPassword('');
      setShowCreateForm(false);
      alert('Identity created and logged in!');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create identity');
    }
  };

  const handleLogin = async () => {
    if (!selectedUserId || !selectedPassword) {
      alert('Please select an identity and enter password');
      return;
    }

    try {
      await switchIdentity(selectedUserId, selectedPassword);
      setSelectedPassword('');
      setSelectedUserId(null);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Login failed');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Logout failed');
    }
  };

  return (
    <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>Identity Manager</h2>
        {onClose && (
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '0',
            }}
          >
            ✕
          </button>
        )}
      </div>

      {/* Current Identity Status */}
      {currentIdentity ? (
        <div style={{ padding: '12px', background: '#d1fae5', borderRadius: '8px' }}>
          <div style={{ fontWeight: '600', color: '#065f46', marginBottom: '8px' }}>
            ✓ Logged in as: {currentIdentity.name}
          </div>
          <button
            onClick={handleLogout}
            disabled={isLoading}
            style={{
              padding: '8px 16px',
              background: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.5 : 1,
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            Logout
          </button>
        </div>
      ) : (
        <div
          style={{ padding: '12px', background: '#fef3c7', borderRadius: '8px', color: '#92400e' }}
        >
          Not logged in
        </div>
      )}

      {/* Create New Identity Form */}
      {showCreateForm && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            padding: '12px',
            background: '#f3f4f6',
            borderRadius: '8px',
          }}
        >
          <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600' }}>
            Create New Identity
          </h3>
          <div>
            <label
              style={{ fontSize: '12px', fontWeight: '600', display: 'block', marginBottom: '4px' }}
            >
              Adjective
            </label>
            <input
              type="text"
              placeholder="e.g. Luminous, Silent, Wild"
              value={newAdjective}
              onChange={(e) => setNewAdjective(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '13px',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div>
            <label
              style={{ fontSize: '12px', fontWeight: '600', display: 'block', marginBottom: '4px' }}
            >
              Noun
            </label>
            <input
              type="text"
              placeholder="e.g. Phoenix, Ocean, Forest"
              value={newNoun}
              onChange={(e) => setNewNoun(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '13px',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div>
            <label
              style={{ fontSize: '12px', fontWeight: '600', display: 'block', marginBottom: '4px' }}
            >
              Verb
            </label>
            <input
              type="text"
              placeholder="e.g. soar, dance, discover"
              value={newVerb}
              onChange={(e) => setNewVerb(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '13px',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div>
            <label
              style={{ fontSize: '12px', fontWeight: '600', display: 'block', marginBottom: '4px' }}
            >
              Identity Name
            </label>
            <input
              type="text"
              placeholder="Display name for this identity"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '13px',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div>
            <label
              style={{ fontSize: '12px', fontWeight: '600', display: 'block', marginBottom: '4px' }}
            >
              Password
            </label>
            <input
              type="password"
              placeholder="Password for login"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '13px',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <button
            onClick={handleCreateIdentity}
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '8px 12px',
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.5 : 1,
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            {isLoading ? 'Creating...' : 'Create Identity'}
          </button>
          {identities.length > 0 && (
            <button
              onClick={() => setShowCreateForm(false)}
              style={{
                width: '100%',
                padding: '6px 12px',
                background: '#e5e7eb',
                color: '#374151',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500',
              }}
            >
              Back to Login
            </button>
          )}
        </div>
      )}

      {/* Available Identities or Create Button */}
      {identities.length > 0 && !showCreateForm ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <h3 style={{ margin: '8px 0', fontSize: '14px', color: '#666' }}>Available Identities</h3>
          {identities.map((id) => (
            <div
              key={id.userId}
              style={{
                padding: '12px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                background: selectedUserId === id.userId ? '#eff6ff' : '#fff',
              }}
            >
              <div style={{ fontWeight: '600', marginBottom: '8px' }}>{id.name}</div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                ID: {id.userId.slice(0, 16)}...
              </div>
              {selectedUserId === id.userId && (
                <div style={{ marginBottom: '8px' }}>
                  <input
                    type="password"
                    placeholder="Enter password"
                    value={selectedPassword}
                    onChange={(e) => setSelectedPassword(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '6px 8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '13px',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              )}
              <button
                onClick={() => {
                  if (selectedUserId === id.userId) {
                    handleLogin();
                  } else {
                    setSelectedUserId(id.userId);
                    setSelectedPassword('');
                  }
                }}
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: '6px 12px',
                  background: '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  opacity: isLoading ? 0.5 : 1,
                  fontSize: '13px',
                  fontWeight: '500',
                }}
              >
                {selectedUserId === id.userId ? 'Login' : 'Select'}
              </button>
            </div>
          ))}
          <button
            onClick={() => setShowCreateForm(true)}
            style={{
              width: '100%',
              padding: '8px 12px',
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500',
              marginTop: '8px',
            }}
          >
            + Create Another Identity
          </button>
        </div>
      ) : !showCreateForm ? (
        <div style={{ color: '#999', textAlign: 'center', padding: '20px' }}>
          <p>No identities found</p>
          <button
            onClick={() => setShowCreateForm(true)}
            style={{
              padding: '8px 16px',
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            Create First Identity
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default IdentityManager;
